import { PlusOutlined } from '@ant-design/icons'
import { Button, Flex, FormInstance, Table, TableColumnsType, Tag } from 'antd'
import { useMemo, useState } from 'react'

import { useTreatmentServiceGetTreatmentPlanRecipeListEndpoint } from 'src/api/queries'
import { Permissions } from 'src/api/requests'
import { Text } from 'src/components'
import { getRecipeStatus } from 'src/features/persons/utils'
import { EXISTING_TREATMENT_PLAN } from 'src/features/persons/utils/constants'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash, formatDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

import { AddRecipeModal } from './AddRecipeModal'

type Props = {
  form?: FormInstance
  treatmentPlan?: string
  hideCreateButton?: boolean
}

export const Recipes = ({ form, treatmentPlan, hideCreateButton }: Props) => {
  const { t } = useLocales()

  const { styles } = useStyles()

  const { hasPermission } = useUser()
  const [isModalOpened, setIsModalOpened] = useState(false)

  const treatmentPlanId =
    treatmentPlan ?? form?.getFieldValue(EXISTING_TREATMENT_PLAN)

  const pagination = useTablePagination()

  const params = {
    treatmentPlanId: String(treatmentPlanId),
    pageNumber: pagination.current,
    pageSize: pagination.pageSize
  }
  const { data, isLoading } =
    useTreatmentServiceGetTreatmentPlanRecipeListEndpoint(
      params,
      [treatmentPlanId],
      {
        enabled: !!treatmentPlanId
      }
    )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'id',
        title: t('consultations.recipe_number'),
        render: (_, __, index) => `${index + 1}`
      },
      {
        key: 'dateIssued',
        title: t('consultations.recipe_date'),
        render: ({ dateIssued }) =>
          dateIssued ? formatDate(dateIssued, dayMonthYearBackSlash) : '-'
      },
      {
        key: 'dispensingDate',
        title: t('stock_administration.release_date'),
        render: ({ dispensingDate }) =>
          dispensingDate
            ? formatDate(dispensingDate, dayMonthYearBackSlash)
            : '-'
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ status }) => {
          return (
            <Tag color={getRecipeStatus(t, status).color} key={status}>
              {getRecipeStatus(t, status).text}
            </Tag>
          )
        }
      }
    ],
    [t, treatmentPlanId]
  )

  const onToggle = () => setIsModalOpened((prev) => !prev)

  return (
    <Flex vertical gap={16} className={styles.treatmentContent}>
      <Flex gap={16} justify="space-between">
        <Text size="xl">{t('consultations.recipes')}</Text>

        {!hideCreateButton &&
          hasPermission(
            Permissions.TREATMENT_TREATMENT_PLAN_RECIPE_CREATE_ALL
          ) && (
            <Button icon={<PlusOutlined />} type="primary" onClick={onToggle}>
              {t('consultations.add_new_recipe')}
            </Button>
          )}
      </Flex>
      <Table
        rowKey="id"
        columns={columns}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        dataSource={data?.data}
        pagination={{
          ...pagination,
          hideOnSinglePage: true,
          total: data?.totalCount
        }}
      />

      {isModalOpened && <AddRecipeModal form={form} onCancel={onToggle} />}
    </Flex>
  )
}
